/**
 * Contact Service - API calls
 * Handles all HTTP requests to the backend contacts endpoints
 */

import api from './api';

class ContactService {
  /**
   * List all contacts with filters
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async listContacts(params = {}) {
    const response = await api.get('/admin/contacts', {
      params: {
        search: params.search || '',
        type: params.type || '',
        category: params.category || '',
        isActive: params.isActive !== false,
        limit: params.limit || 20,
        offset: params.offset || 0,
      }
    });
    return response.data;
  }

  /**
   * Get a single contact by ID
   * @param {number} id - Contact ID
   * @returns {Promise<Object>}
   */
  async getContact(id) {
    const response = await api.get(`/admin/contacts/${id}`);
    return response.data;
  }

  /**
   * Get invoices for a contact
   * @param {number} id - Contact ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async getContactInvoices(id, params = {}) {
    const response = await api.get(`/admin/contacts/${id}/invoices`, {
      params: {
        limit: params.limit || 20,
        offset: params.offset || 0,
      }
    });
    return response.data;
  }

  /**
   * Search contacts (for autocomplete)
   * @param {string} query - Search query
   * @param {string} type - Filter by type (optional)
   * @param {number} limit - Max results
   * @returns {Promise<Object>}
   */
  async searchContacts(query, type = '', limit = 10) {
    if (query.length < 2) {
      return { success: true, data: [] };
    }

    const response = await api.get('/admin/contacts/search', {
      params: {
        q: query,
        type: type,
        limit: limit,
      }
    });
    return response.data;
  }

  /**
   * Create a new contact
   * @param {Object} contactData - Contact data
   * @returns {Promise<Object>}
   */
  async createContact(contactData) {
    const response = await api.post('/admin/contacts', contactData);
    return response.data;
  }

  /**
   * Update a contact
   * @param {number} id - Contact ID
   * @param {Object} contactData - Updated fields
   * @returns {Promise<Object>}
   */
  async updateContact(id, contactData) {
    const response = await api.put(`/admin/contacts/${id}`, contactData);
    return response.data;
  }

  /**
   * Delete a contact
   * @param {number} id - Contact ID
   * @returns {Promise<Object>}
   */
  async deleteContact(id) {
    const response = await api.delete(`/admin/contacts/${id}`);
    return response.data;
  }
}

export default new ContactService();
